import { useEffect, useState } from "react"
import PropTypes from "prop-types"
import {
  Alert,
  Button,
  Form,
  FormFeedback,
  Input,
  Label,
} from "reactstrap"
import { toast } from "react-toastify"
import { useFormik } from "formik"
import * as Yup from "yup"

import ThemeModal from "components/Common/ThemeModal"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"
import { useAuth } from "hooks/authHook"
import { useEditUser } from "hooks/userHook"

// Resolves the slug for the viewer so the modal can show "who" is editing.
const isOwner = (authUser, targetUser) =>
  !!authUser && !!targetUser && authUser.id === targetUser.id

const canEdit = (authUser, targetUser, hasPermission) => {
  if (!targetUser) return false
  if (isOwner(authUser, targetUser)) return true
  return hasPermission(ACCESS.USER.EDIT_USER)
}

const canManageRoles = (authUser, targetUser, hasPermission) => {
  if (!targetUser) return false
  if (isOwner(authUser, targetUser)) return true
  return hasPermission(ACCESS.USER.MANAGE_USER_ROLES)
}

const profileSchema = (isSelf) =>
  Yup.object({
    name: Yup.string()
      .trim()
      .min(2, "Name must be at least 2 characters")
      .matches(
        /^[a-zA-Z\s.'-]+$/,
        "Name can only contain letters, spaces, and basic punctuation"
      )
      .required("Name is required"),
    username: Yup.string()
      .trim()
      .min(3, "Username must be at least 3 characters")
      .max(50)
      .matches(
        /^[a-zA-Z0-9_]+$/,
        "Username can only contain letters, numbers, and underscores"
      )
      .required("Username is required"),
    email: Yup.string()
      .trim()
      .email("Enter a valid email address")
      .nullable(),
  })

const UserAccountEditor = ({ user, onSaved, label = "Change Profile", size = "sm", outline = true }) => {
  const { user: authUser, hasPermission } = useAuth()
  const [open, setOpen] = useState(false)
  const { mutateAsync: editUser, isPending } = useEditUser()

  const allowed = canEdit(authUser, user, hasPermission)
  const rolesAllowed = canManageRoles(authUser, user, hasPermission)

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: user?.name || "",
      username: user?.username || "",
      email: user?.email || "",
    },
    validationSchema: profileSchema(isOwner(authUser, user)),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async values => {
      try {
        const res = await editUser({
          id: user.id,
          name: values.name.trim(),
          username: values.username.trim(),
          email: values.email?.trim() || null,
        })
        toast.success(res?.message ?? "Profile updated successfully.")
        setOpen(false)
        onSaved?.(res)
      } catch (error) {
        const msg =
          error?.response?.data?.message ?? "Failed to update profile."
        toast.error(msg)
      }
    },
  })

  // Reset form when the target user changes (e.g., refetch returned a new record)
  useEffect(() => {
    if (open) {
      formik.resetForm({
        values: {
          name: user?.name || "",
          username: user?.username || "",
          email: user?.email || "",
        },
      })
    }
  }, [open, user?.id, user?.name, user?.username, user?.email, formik.resetForm])

  if (!allowed) return null

  return (
    <>
      <Button
        color={outline ? "info" : "info"}
        outline={outline}
        size={size}
        onClick={() => setOpen(true)}
        id={`change-profile-${user?.id}`}
      >
        <i className="bx bx-edit-alt me-1" />
        {label}
      </Button>

      <ThemeModal
        isOpen={open}
        toggle={() => !isPending && setOpen(false)}
        title={`Change Profile — ${user?.name || user?.username || "User"}`}
      >
        {!rolesAllowed && (
          <Alert color="info" className="small mb-3">
            <i className="bx bx-info-circle me-1" />
            You can update your basic account details. Role and permission
            changes require elevated access.
          </Alert>
        )}

        <Form
          onSubmit={e => {
            e.preventDefault()
            formik.handleSubmit()
          }}
        >
          <div className="mb-3">
            <Label>
              Full Name <span className="text-danger">*</span>
            </Label>
            <Input
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              invalid={!!formik.errors.name}
              disabled={isPending}
            />
            {formik.errors.name && (
              <FormFeedback>{formik.errors.name}</FormFeedback>
            )}
          </div>

          <div className="mb-3">
            <Label>
              Username <span className="text-danger">*</span>
            </Label>
            <Input
              name="username"
              value={formik.values.username}
              onChange={formik.handleChange}
              invalid={!!formik.errors.username}
              disabled={isPending}
            />
            {formik.errors.username && (
              <FormFeedback>{formik.errors.username}</FormFeedback>
            )}
            <small className="text-muted">
              Used for login. Changing it will require the user to use the new
              username on next sign-in.
            </small>
          </div>

          <div className="mb-3">
            <Label>Email</Label>
            <Input
              type="email"
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              invalid={!!formik.errors.email}
              disabled={isPending}
            />
            {formik.errors.email && (
              <FormFeedback>{formik.errors.email}</FormFeedback>
            )}
          </div>

          <div className="text-end">
            <Button
              color="secondary"
              outline
              type="button"
              className="me-2"
              onClick={() => setOpen(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <CanView access={[ACCESS.USER.EDIT_USER]}>
              <Button color="info" type="submit" disabled={isPending}>
                {isPending ? "Saving..." : "Save Changes"}
              </Button>
            </CanView>
          </div>
        </Form>
      </ThemeModal>
    </>
  )
}

UserAccountEditor.propTypes = {
  user: PropTypes.object,
  onSaved: PropTypes.func,
  label: PropTypes.string,
  size: PropTypes.string,
  outline: PropTypes.bool,
}

export default UserAccountEditor
